import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Collapse } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  Protocol,
  instanceOfTeamRecord,
  instanceOfSingleRaceRecord,
  SingleRaceRecord,
  ChallengeFormula,
  Participant,
} from "/src/domain/challenges/ChallengeProtocolData";
import ChallengeBanner from "/src/ui/challengeProtocol/ChallengeBanner";
import { TrainerBanner } from "/src/ui/shared/TrainerBanner";
import { theme } from "/src/assets/theme";
import { User } from "/src/domain/shared/User";
import {
  getHeaderContentClassByRecord,
  ProtocolHeaderContentProps,
} from "./ProtocolHeaderContent";
import { ProtocolHeader } from "./ProtocolHeader";
import { ExpandableRecordView } from "./record/ExpandableRecordView";
import { PageContext, PageContextProps } from "/src/contexts/Page";
import cs from "classnames";
import {
  ChallengeContext,
  ChallengeContextProps,
} from "/src/contexts/ChallengeContext";
import { instanceOfDsqRecord } from "../../domain/challenges/ChallengeProtocolData";
import { useTranslation } from "react-i18next";
import { ProtocolPaginationView } from "./ProtocolPaginationView";

const CHALLENGE_BANNER_INDEX = 10;
const RECORDS_PER_PAGE = 100;

interface ProtocolViewProps {
  protocol: Protocol;
  challengeType: ChallengeFormula;
  isExpand: boolean;
  onProtocolClick: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
  currentUser?: User;
}

export const ProtocolView: FunctionComponent<ProtocolViewProps> = ({
  protocol,
  challengeType,
  isExpand,
  onProtocolClick,
  containerRef,
  currentUser,
}) => {
  const classes = useStyles();
  const { pageConfig } = useContext(PageContext) as PageContextProps;
  const { chosenClub } = useContext(ChallengeContext) as ChallengeContextProps;
  const { t } = useTranslation();

  const isParticipantClub = (
    participant: Participant,
    club?: string
  ): Boolean => {
    if (club == "0") {
      return participant.clubId == null;
    }
    return participant.clubId == chosenClub;
  };

  const records = useMemo(() => {
    return protocol.records.filter((record) => {
      if (chosenClub == null) return true;

      if (
        (instanceOfSingleRaceRecord(record) || instanceOfDsqRecord(record)) &&
        !isParticipantClub(record.participant, chosenClub)
      )
        return false;

      if (
        instanceOfTeamRecord(record) &&
        record.records.every(
          (r) => !isParticipantClub(r.participant, chosenClub)
        )
      )
        return false;

      return true;
    });
  }, [protocol.records, chosenClub]);

  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [chosenClub]);

  useEffect(() => {
    setTotalPages(Math.ceil(records.length / RECORDS_PER_PAGE));
  }, [records]);

  const displayedRecords = records.slice(
    currentPage * RECORDS_PER_PAGE,
    (currentPage + 1) * RECORDS_PER_PAGE
  );

  if (displayedRecords.length === 0) return null;

  const HeaderContent: FunctionComponent<ProtocolHeaderContentProps> =
    getHeaderContentClassByRecord(records[0]);
  if (!HeaderContent) return null;

  return (
    <div className={classes.container}>
      <ProtocolHeader
        protocol={protocol}
        records={records}
        isExpand={isExpand}
        onClick={onProtocolClick}
        containerRef={containerRef}
      />
      <Collapse in={isExpand} timeout={300}>
        <HeaderContent
          isTimeRace={
            instanceOfSingleRaceRecord(records[0]) &&
            Boolean((records[0] as SingleRaceRecord)?.time)
          }
          mainRaceLabel={
            instanceOfSingleRaceRecord(records[0])
              ? (records[0] as SingleRaceRecord).parameters[0]?.label
              : ""
          }
          challengeType={challengeType}
        />
        {displayedRecords.map((record, index, arr) => {
          const placeByOrder = chosenClub === null ? undefined : currentPage * 100 + (index + 1);
          const lastIndex = arr.length - 1;
          const isNeedToShowPostcardAds =
            ((index > 0 && index % CHALLENGE_BANNER_INDEX === 0) ||
              (lastIndex < CHALLENGE_BANNER_INDEX && index === lastIndex)) &&
            !pageConfig?.addsDisabled;

          const isNotInTop5 = record.place && record.place > 5;
          const isNeedToShowTrainersAds =
            instanceOfSingleRaceRecord(record) &&
            record.participant.id === currentUser?.id &&
            !currentUser?.currentTrainerId &&
            isNotInTop5 &&
            !pageConfig?.addsDisabled &&
            chosenClub == null;

          const recordKey = instanceOfTeamRecord(record)
            ? record.name
            : `${(record as SingleRaceRecord).trackId}-${index}`;

          return (
            <div key={recordKey} className={classes.participantBlock}>
              {isNeedToShowPostcardAds && <ChallengeBanner />}
              {isNeedToShowTrainersAds && (
                <TrainerBanner
                  className={cs({
                    [classes.trainerBanner]: isNeedToShowPostcardAds,
                  })}
                />
              )}
              <ExpandableRecordView
                placeByOrder={placeByOrder}
                challengeType={challengeType}
                record={record}
                currentUser={currentUser}
              />
            </div>
          );
        })}
        <ProtocolPaginationView
          currentPage={currentPage}
          total={totalPages}
          perPage={RECORDS_PER_PAGE}
          totalElements={records.length}
          setCurrentPage={setCurrentPage}
        />
      </Collapse>
    </div>
  );
};

const useStyles = makeStyles((muiTheme) => ({
  container: {
    paddingBottom: "1.5rem",
  },
  participantBlock: {
    color: theme.primaryColor,
    "&:nth-child(even)": {
      backgroundColor: theme.lightPrimaryColor,
    },
  },
  trainerBanner: {
    marginTop: "1rem",
  },
}));

export default ProtocolView;
